import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useRef } from 'react';
import useNetworkStatus from '../hooks/useNetworkStatus';
import { useToastContext } from '../../layout/context/toast-context';
const NetworkStatusContext = createContext(undefined);
export const NetworkStatusProvider = ({ children, showNotifications = true, enableHealthCheck = false, onStatusChange, }) => {
    const networkStatus = useNetworkStatus({
        enableHealthCheck,
    });
    const previousOnlineStatusRef = useRef(null);
    useEffect(() => {
        // Initialize previous status on mount
        if (previousOnlineStatusRef.current === null) {
            previousOnlineStatusRef.current = networkStatus.isOnline;
            return;
        }
        const statusChanged = previousOnlineStatusRef.current !== networkStatus.isOnline;
        if (statusChanged) {
            // Call optional status change callback
            if (onStatusChange) {
                onStatusChange(networkStatus.isOnline);
            }
            previousOnlineStatusRef.current = networkStatus.isOnline;
        }
    }, [networkStatus.isOnline, onStatusChange]);
    const value = {
        networkStatus,
    };
    return (_jsxs(NetworkStatusContext.Provider, { value: value, children: [_jsx(NetworkStatusNotifications, { networkStatus: networkStatus, showNotifications: showNotifications }), children, !networkStatus.isOnline && (_jsxs("div", { style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#ff9800',
                    color: 'white',
                    padding: '8px 16px',
                    textAlign: 'center',
                    zIndex: 9999,
                    fontSize: '14px',
                    fontWeight: 500,
                }, children: [_jsx("i", { className: "pi pi-exclamation-triangle", style: { marginRight: '8px' } }), "You are currently offline. Some features may be unavailable."] }))] }));
};
// Separate component that uses toast context for notifications
// This component should be rendered inside ToastContextProvider
const NetworkStatusNotifications = ({ networkStatus, showNotifications }) => {
    const previousOnlineStatusRef = useRef(null);
    const notificationShownRef = useRef(false);
    // Use toast context - will throw if not inside ToastContextProvider
    // This is expected since we wrap NetworkStatusProvider inside ToastContextProvider in main.tsx
    const toastContext = useToastContext();
    useEffect(() => {
        if (!showNotifications) {
            return;
        }
        // Initialize previous status on mount
        if (previousOnlineStatusRef.current === null) {
            previousOnlineStatusRef.current = networkStatus.isOnline;
            return;
        }
        const statusChanged = previousOnlineStatusRef.current !== networkStatus.isOnline;
        if (statusChanged) {
            if (networkStatus.isOnline) {
                // Connection restored
                toastContext.showToast({
                    severity: 'success',
                    summary: 'Connection Restored',
                    detail: 'You are back online. All features are now available.',
                    life: 3000,
                });
                notificationShownRef.current = true;
            }
            else {
                // Connection lost
                toastContext.showToast({
                    severity: 'warn',
                    summary: 'Connection Lost',
                    detail: 'You are offline. Some features may be limited until your connection is restored.',
                    life: 3000,
                });
                notificationShownRef.current = true;
            }
            previousOnlineStatusRef.current = networkStatus.isOnline;
        }
        else if (networkStatus.isOnline && notificationShownRef.current) {
            // Dismiss offline notification if we're back online
            notificationShownRef.current = false;
        }
    }, [networkStatus.isOnline, showNotifications, toastContext]);
    return null;
};
export const useNetworkStatusContext = () => {
    const context = useContext(NetworkStatusContext);
    if (!context) {
        throw new Error('useNetworkStatusContext must be used within NetworkStatusProvider');
    }
    return context;
};
export default NetworkStatusProvider;
